/**
 * Torpedo lock tone (plan G11, audio-direction §3): while the lock builds, a beep whose rate and pitch follow lock progress
 * (`intervalStart` → `intervalEnd` seconds between beeps); once locked, the solid tone. Pure state,
 * allocation-free; the audio feature turns the result into mixer calls.
 */
import type { LockToneConfig } from './sfxSchema'

export interface LockToneState {
  /** Seconds until the next beep (≤ 0: beep on the next locking step). */
  untilBeep: number
  /** Whether the solid tone should be on. */
  solid: boolean
}

export function createLockToneState(): LockToneState {
  return { untilBeep: 0, solid: false }
}

/** Playback rate of a beep at `progress` 0..1: the tone rises as the lock builds (audio-direction §3). */
export function beepRate(progress: number, cfg: LockToneConfig): number {
  const p = progress < 0 ? 0 : progress > 1 ? 1 : progress
  return 1 + cfg.pitchRise * p
}

/** Seconds between beeps at `progress` 0..1 (clamped). */
export function beepInterval(progress: number, cfg: LockToneConfig): number {
  const p = progress < 0 ? 0 : progress > 1 ? 1 : progress
  return cfg.intervalStart + (cfg.intervalEnd - cfg.intervalStart) * p
}

/**
 * Advances the tone by `dt` seconds. `progress` is the lock progress 0..1 (0 = no lock building) and `locked`
 * whether the lock is complete. Returns true when a beep plays this step; `state.solid` says whether the
 * solid tone should be on.
 */
export function stepLockTone(state: LockToneState, progress: number, locked: boolean, dt: number, cfg: LockToneConfig): boolean {
  if (locked) {
    state.solid = true
    state.untilBeep = 0
    return false
  }
  state.solid = false
  if (!(progress > 0)) {
    state.untilBeep = 0
    return false
  }
  state.untilBeep -= dt
  if (state.untilBeep > 0) return false
  state.untilBeep += beepInterval(progress, cfg)
  if (state.untilBeep <= 0) state.untilBeep = beepInterval(progress, cfg)
  return true
}
